import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  MessageSquare, Loader2, Send, Trash2, X, ChevronDown, ChevronUp,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
};

/**
 * Per-line comments thread for items inside a proposal.
 * - kind: "feature" | "component" | "deliverable" | "general"
 * - index: 0-based, or -1 for general
 * - email: client email (gates list/post/delete)
 */
export default function ProposalCommentThread({ projectId, kind, index, email, refreshKey = 0, compact = false }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [body, setBody] = useState("");
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!email) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/projects/${projectId}/proposal-comments`, { params: { email } });
      const filtered = (res.data || []).filter((c) => c.item_kind === kind && (c.item_index ?? -1) === (index ?? -1));
      setComments(filtered);
    } catch (e) {
      // silent — typically 403 if email is missing
    } finally { setLoading(false); }
  }, [projectId, kind, index, email]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const submit = async (e) => {
    e?.preventDefault?.();
    setError("");
    if (body.trim().length < 2) { setError("Comment is too short"); return; }
    setPosting(true);
    try {
      await axios.post(`${API}/projects/${projectId}/proposal-comments`, {
        email, item_kind: kind, item_index: index ?? -1, body: body.trim(),
      });
      setBody("");
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || "Could not post");
    } finally { setPosting(false); }
  };

  const remove = async (commentId) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await axios.delete(`${API}/projects/${projectId}/proposal-comments/${commentId}`, { params: { email } });
      await load();
    } catch (e) { alert(e.response?.data?.detail || "Could not delete"); }
  };

  const count = comments.length;
  const ownEmail = (email || "").trim().toLowerCase();

  if (!email) {
    return (
      <button type="button" disabled
        className="inline-flex items-center gap-1 text-[10px] font-semibold text-zinc-400 cursor-not-allowed">
        <MessageSquare size={10} /> Sign in to comment
      </button>
    );
  }

  return (
    <div className="mt-1.5">
      <button type="button" onClick={() => setOpen((v) => !v)} data-testid={`btn-toggle-comment-${kind}-${index}`}
        className={`inline-flex items-center gap-1 text-[10px] font-semibold transition-colors ${
          count > 0 ? "text-blue-600 hover:text-blue-800" : "text-zinc-400 hover:text-zinc-700"
        }`}>
        <MessageSquare size={10} />
        {count > 0 ? `${count} comment${count === 1 ? "" : "s"}` : "Comment"}
        {open ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
      </button>

      {open && (
        <div className="mt-2 ml-4 pl-3 border-l-2 border-zinc-100" data-testid={`comments-thread-${kind}-${index}`}>
          {loading ? <div className="py-1"><Loader2 size={11} className="animate-spin text-zinc-400" /></div>
            : comments.length === 0 ? <p className="text-[11px] text-zinc-400 italic py-1">No comments yet — start the discussion.</p>
            : (
              <div className="space-y-2">
                {comments.map((c) => (
                  <div key={c.comment_id} data-testid={`comment-${c.comment_id}`}
                    className={`text-xs rounded-lg p-2 ${c.author_kind === "admin" ? "bg-zinc-950 text-white" : "bg-zinc-50 text-zinc-800 border border-zinc-100"}`}>
                    <div className={`flex items-center gap-2 text-[10px] mb-1 ${c.author_kind === "admin" ? "text-zinc-300" : "text-zinc-500"}`}>
                      <span className="font-bold uppercase tracking-wider">{c.author_kind === "admin" ? "Team" : "You"}</span>
                      <span>· {fmtTime(c.created_at)}</span>
                      {c.author_kind === "client" && c.author_id === ownEmail && (
                        <button onClick={() => remove(c.comment_id)} data-testid={`btn-delete-comment-${c.comment_id}`}
                          className="ml-auto text-zinc-400 hover:text-red-500"><Trash2 size={10} /></button>
                      )}
                    </div>
                    <div className="whitespace-pre-wrap leading-relaxed">{c.body}</div>
                  </div>
                ))}
              </div>
            )
          }
          <form onSubmit={submit} className="mt-2 flex gap-1.5">
            <input data-testid={`input-comment-${kind}-${index}`} value={body} onChange={(e) => setBody(e.target.value)}
              placeholder="Add a comment…" maxLength={2000}
              className={`flex-1 px-2 py-1 bg-white border border-zinc-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-zinc-900 ${compact ? "" : ""}`} />
            <button type="submit" disabled={posting || body.trim().length < 2} data-testid={`btn-post-comment-${kind}-${index}`}
              className="px-2.5 py-1 bg-zinc-950 text-white text-xs font-semibold rounded-lg hover:bg-zinc-800 disabled:opacity-40 inline-flex items-center gap-1">
              {posting ? <Loader2 size={10} className="animate-spin" /> : <Send size={10} />}
            </button>
          </form>
          {error && <div className="mt-1 text-[10px] text-red-600">{error}</div>}
        </div>
      )}
    </div>
  );
}
